import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import pdfDataCache from '../documents/pdfDataCache';

// Type definition based on the document_filter table schema
export const FilterType = {
  id: '',
  remark: null,
  upperdepth: null,
  upperdepth_uom: null,
  upperdepth_condition: null, // Default handled by DB? Check if needed here.
  lowerdepth: null,
  lowerdepth_uom: null,
  lowerdepth_condition: null, // Default handled by DB? Check if needed here.
  borehole_id: null,
  created_at: null,
  updated_at: null,
  document_id: null,
  layer_type: null,          // Default handled by DB? Check if needed here.
  boreprocess_id: null,
  samplingdevice: null,
  MeetObjectSoort: null,    // Default handled by DB? Check if needed here.
  project_id: null,
  startTime: null,
  name: null,
  heightTopFiltertube_value: null,
  heightTopFiltertube_uom: null,
  filtertubeDiameter_value: null,
  filtertubeDiameter_uom: null,
  filtertubeLength_value: null,
  filtertubeLength_uom: null,
  filtertubeMaterial: null,
};

// Hook for fetching filters
export const useFilters = (param = {}) => {
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define the core fetching logic using useCallback
  const fetchFiltersInternal = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const documentId = param.document_id || 'global';
      const tabName = 'filters'; // Distinct tab name for filters
      const extraKey = JSON.stringify(param);
      const cachedData = pdfDataCache.get(documentId, tabName, extraKey);

      if (cachedData) {
        console.log('Using cached filters data');
        setFilters(cachedData);
        setLoading(false);
        return; // Exit if using cache
      }

      let query = supabase
        .from('document_filter')
        // Use explicit and CORRECTED foreign key constraint names based on provided schema
        .select(`
          *,
          upperdepth_uom:document_domainvalueslookup!document_filter_upperdepth_uom_fkey(*),
          upperdepth_condition:document_domainvalueslookup!document_filter_upperdepth_condition_fkey(*),
          lowerdepth_uom:document_domainvalueslookup!document_filter_lowerdepth_uom_fkey(*),
          lowerdepth_condition:document_domainvalueslookup!document_filter_lowerdepth_condition_fkey(*),
          MeetObjectSoort:document_domainvalueslookup!document_filter_MeetObjectSoort_fkey(*),
          heightTopFiltertube_uom:document_domainvalueslookup!document_filter_heightTopFiltertube_uom_fkey(*),
          filtertubeDiameter_uom:document_domainvalueslookup!document_filter_filtertubeDiameter_uom_fkey(*),
          filtertubeLength_uom:document_domainvalueslookup!document_filter_filtertubeLength_uom_fkey(*)
          // Removed layer_type, boreprocess, samplingdevice as they don't exist in the table
        `);

      // Apply filters based on parameters
      if (param.borehole_id) query = query.eq('borehole_id', param.borehole_id);
      if (param.document_id) query = query.eq('document_id', param.document_id);
      if (param.layer_type) query = query.eq('layer_type', param.layer_type);
      if (param.boreprocess_id) query = query.eq('boreprocess_id', param.boreprocess_id);
      if (param.samplingdevice) query = query.eq('samplingdevice', param.samplingdevice);
      if (param.MeetObjectSoort) query = query.eq('MeetObjectSoort', param.MeetObjectSoort); // Case sensitive column name might be needed

      // Generic filters support
      if (param.filters && Array.isArray(param.filters)) {
        for (const filter of param.filters) {
          if (filter.operator === 'eq') query = query.eq(filter.column, filter.value);
          else if (filter.operator === 'is') query = query.is(filter.column, filter.value);
          // Add other operators as needed (neq, gt, lt, etc.)
        }
      }

      const { data, error: queryError } = await query;
      if (queryError) throw queryError;

      pdfDataCache.set(documentId, tabName, data, extraKey);
      setFilters(data);

    } catch (err) {
      console.error("Error fetching filters:", err);
      setError(err.message || 'Fout bij ophalen filters');
    } finally {
      setLoading(false);
    }
  }, [param]); // Dependency array includes param

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates on unmounted component

    // Initial fetch using the useCallback function
    fetchFiltersInternal();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMounted) {
        console.log("Tab became visible, refetching filters...");
        // Refetch using the useCallback function
        fetchFiltersInternal();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchFiltersInternal]); // useEffect depends on the stable fetch function

  // Return the refetch function along with state
  return { filters, loading, error, refetch: fetchFiltersInternal };
};

// Helper function to fetch a single filter by ID
export const getFilterById = async (id) => {
  const cacheKey = 'filter_by_id';
  const cachedData = pdfDataCache.get('global', cacheKey, id);
  if (cachedData) {
    return cachedData;
  }

  const { data, error } = await supabase
    .from('document_filter')
    .select(`
      *,
      upperdepth_uom:document_domainvalueslookup!document_filter_upperdepth_uom_fkey(*),
      upperdepth_condition:document_domainvalueslookup!document_filter_upperdepth_condition_fkey(*),
      lowerdepth_uom:document_domainvalueslookup!document_filter_lowerdepth_uom_fkey(*),
      lowerdepth_condition:document_domainvalueslookup!document_filter_lowerdepth_condition_fkey(*),
      MeetObjectSoort:document_domainvalueslookup!document_filter_MeetObjectSoort_fkey(*),
      heightTopFiltertube_uom:document_domainvalueslookup!document_filter_heightTopFiltertube_uom_fkey(*),
      filtertubeDiameter_uom:document_domainvalueslookup!document_filter_filtertubeDiameter_uom_fkey(*),
      filtertubeLength_uom:document_domainvalueslookup!document_filter_filtertubeLength_uom_fkey(*)
    `)
    .eq('id', id)
    .single();

  if (error) throw error;

  pdfDataCache.set('global', cacheKey, data, id);
  return data;
};

// Helper function to create a new filter
export const createFilter = async (filterData) => {
  const { data, error } = await supabase
    .from('document_filter')
    .insert([filterData])
    .select(`
      *,
      upperdepth_uom:document_domainvalueslookup!document_filter_upperdepth_uom_fkey(*),
      upperdepth_condition:document_domainvalueslookup!document_filter_upperdepth_condition_fkey(*),
      lowerdepth_uom:document_domainvalueslookup!document_filter_lowerdepth_uom_fkey(*),
      lowerdepth_condition:document_domainvalueslookup!document_filter_lowerdepth_condition_fkey(*),
      MeetObjectSoort:document_domainvalueslookup!document_filter_MeetObjectSoort_fkey(*),
      heightTopFiltertube_uom:document_domainvalueslookup!document_filter_heightTopFiltertube_uom_fkey(*),
      filtertubeDiameter_uom:document_domainvalueslookup!document_filter_filtertubeDiameter_uom_fkey(*),
      filtertubeLength_uom:document_domainvalueslookup!document_filter_filtertubeLength_uom_fkey(*)
    `); // Use select() to get the created record back, including foreign key lookups

  if (error) throw error;

  // Invalidate relevant caches
  if (filterData.document_id) {
    pdfDataCache.clearTab(filterData.document_id, 'filters');
  }
  if (filterData.borehole_id) {
    pdfDataCache.clearTab('global', 'filters_by_borehole_' + filterData.borehole_id);
  }
  // Consider clearing global cache if filter might appear in general lists
  pdfDataCache.clearTab('global', 'filters'); 

  return data[0]; // Return the newly created filter object
};

// Helper function to update an existing filter
export const updateFilter = async (id, updates) => {
  const { data, error } = await supabase
    .from('document_filter')
    .update(updates)
    .eq('id', id)
    .select(`
      *,
      upperdepth_uom:document_domainvalueslookup!document_filter_upperdepth_uom_fkey(*),
      upperdepth_condition:document_domainvalueslookup!document_filter_upperdepth_condition_fkey(*),
      lowerdepth_uom:document_domainvalueslookup!document_filter_lowerdepth_uom_fkey(*),
      lowerdepth_condition:document_domainvalueslookup!document_filter_lowerdepth_condition_fkey(*),
      MeetObjectSoort:document_domainvalueslookup!document_filter_MeetObjectSoort_fkey(*),
      heightTopFiltertube_uom:document_domainvalueslookup!document_filter_heightTopFiltertube_uom_fkey(*),
      filtertubeDiameter_uom:document_domainvalueslookup!document_filter_filtertubeDiameter_uom_fkey(*),
      filtertubeLength_uom:document_domainvalueslookup!document_filter_filtertubeLength_uom_fkey(*)
    `); // Use select() to get the updated record back, including foreign key lookups

  if (error) throw error;

  // Invalidate relevant caches after update
  pdfDataCache.clearTab('global', 'filter_by_id'); // Clear specific filter cache
  if (data && data[0]) {
    const updatedFilter = data[0];
    if (updatedFilter.document_id) {
      pdfDataCache.clearTab(updatedFilter.document_id, 'filters');
    }
    if (updatedFilter.borehole_id) {
      pdfDataCache.clearTab('global', 'filters_by_borehole_' + updatedFilter.borehole_id);
    }
    // Consider clearing global cache if filter might appear in general lists
    pdfDataCache.clearTab('global', 'filters');
  } else {
      // Fallback if select() fails or returns null/empty
      console.warn("Filter update succeeded but couldn't get updated data for cache invalidation.")
      pdfDataCache.clear(); // Clear all cache as a precaution
  }


  return data ? data[0] : null; // Return the updated filter object or null
};

// Helper function to delete a filter
export const deleteFilter = async (id) => {
  // Fetch the filter first to know which caches to invalidate
  let filterToDelete = null;
  try {
      filterToDelete = await getFilterById(id);
  } catch(err) {
      console.warn("Could not fetch filter before deletion:", err);
      // Proceed with deletion, but cache invalidation might be less specific
  }

  const { error } = await supabase
    .from('document_filter')
    .delete()
    .eq('id', id);

  if (error) throw error;

  // Invalidate relevant caches
  pdfDataCache.clearTab('global', 'filter_by_id'); // Clear specific filter cache
  if (filterToDelete) {
    if (filterToDelete.document_id) {
      pdfDataCache.clearTab(filterToDelete.document_id, 'filters');
    }
    if (filterToDelete.borehole_id) {
      pdfDataCache.clearTab('global', 'filters_by_borehole_' + filterToDelete.borehole_id);
    }
  }
  // Consider clearing global cache if filter might appear in general lists
  pdfDataCache.clearTab('global', 'filters');


  return true; // Indicate successful deletion
};

// Helper function to get filters for a specific borehole
export const getFiltersByBoreholeId = async (boreholeId) => {
  const cacheKey = 'filters_by_borehole_' + boreholeId;
  const cachedData = pdfDataCache.get('global', cacheKey, '');
  if (cachedData) {
    return cachedData;
  }

  const { data, error } = await supabase
    .from('document_filter')
    .select(`
      *,
      upperdepth_uom:document_domainvalueslookup!document_filter_upperdepth_uom_fkey(*),
      upperdepth_condition:document_domainvalueslookup!document_filter_upperdepth_condition_fkey(*),
      lowerdepth_uom:document_domainvalueslookup!document_filter_lowerdepth_uom_fkey(*),
      lowerdepth_condition:document_domainvalueslookup!document_filter_lowerdepth_condition_fkey(*),
      MeetObjectSoort:document_domainvalueslookup!document_filter_MeetObjectSoort_fkey(*),
      heightTopFiltertube_uom:document_domainvalueslookup!document_filter_heightTopFiltertube_uom_fkey(*),
      filtertubeDiameter_uom:document_domainvalueslookup!document_filter_filtertubeDiameter_uom_fkey(*),
      filtertubeLength_uom:document_domainvalueslookup!document_filter_filtertubeLength_uom_fkey(*)
    `)
    .eq('borehole_id', boreholeId);

  if (error) throw error;

  pdfDataCache.set('global', cacheKey, data, '');
  return data;
};

// Helper function to get filters by document id
export const getFiltersByDocumentId = async (documentId) => {
  const cacheKey = 'filters'; // Use the standard tab name for document-specific filters
  const cachedData = pdfDataCache.get(documentId, cacheKey, '');
  if (cachedData) {
    return cachedData;
  }

  const { data, error } = await supabase
    .from('document_filter')
    .select(`
      *,
      upperdepth_uom:document_domainvalueslookup!document_filter_upperdepth_uom_fkey(*),
      upperdepth_condition:document_domainvalueslookup!document_filter_upperdepth_condition_fkey(*),
      lowerdepth_uom:document_domainvalueslookup!document_filter_lowerdepth_uom_fkey(*),
      lowerdepth_condition:document_domainvalueslookup!document_filter_lowerdepth_condition_fkey(*),
      MeetObjectSoort:document_domainvalueslookup!document_filter_MeetObjectSoort_fkey(*),
      heightTopFiltertube_uom:document_domainvalueslookup!document_filter_heightTopFiltertube_uom_fkey(*),
      filtertubeDiameter_uom:document_domainvalueslookup!document_filter_filtertubeDiameter_uom_fkey(*),
      filtertubeLength_uom:document_domainvalueslookup!document_filter_filtertubeLength_uom_fkey(*)
    `)
    .eq('document_id', documentId);

  if (error) throw error;

  pdfDataCache.set(documentId, cacheKey, data, '');
  return data;
};

// Helper function to invalidate filter caches
export const invalidateFilterCache = (documentId = null) => {
  pdfDataCache.clearTab('global', 'filter_by_id');
  if (documentId) {
    pdfDataCache.clearTab(documentId, 'filters');
  } else {
    // Clear potentially relevant global caches if no document ID is provided
    // This might include clearing borehole-specific caches if feasible,
    // but a broad clear might be necessary without specific IDs.
    pdfDataCache.clear(); // Consider a more targeted approach if performance becomes an issue
  }
};
